import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { 
  Trophy, 
  Award, 
  Users, 
  TrendingUp, 
  Calendar, 
  MapPin, 
  ArrowLeft, 
  Share2,
  UserPlus,
  ExternalLink,
  Eye,
  X,
  CheckCircle
} from 'lucide-react'
import { 
  getShareableLinkData,
  getSharedUserProgress,
  getTripById,
  getTripParticipants,
  getGroupShareData,
  joinGroup,
  getUserSpottedTeams
} from '@/lib/firestore'
import { Leaderboard } from '@/components/ui/leaderboard'
import type { ShareableLink, Trip, Team, User, Group, GroupMember, GroupLeaderboardEntry } from '@/types'
import { Button } from '@/components/ui/button'

export default function SharedPage() {
  const { shareToken } = useParams<{ shareToken: string }>()
  const navigate = useNavigate()
  const { user } = useUser()
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [shareData, setShareData] = useState<ShareableLink | null>(null)
  const [groupData, setGroupData] = useState<{
    group: Group | null
    members: GroupMember[]
    leaderboard: GroupLeaderboardEntry[]
  } | null>(null)
  const [isJoining, setIsJoining] = useState(false)
  const [selectedUser, setSelectedUser] = useState<string | null>(null)
  const [selectedUserTeams, setSelectedUserTeams] = useState<(Team & { spottedAt: Date })[]>([])
  const [loadingUserTeams, setLoadingUserTeams] = useState(false)
  const [userData, setUserData] = useState<{
    user: User | null;
    spottedTeams: (Team & { spottedAt: Date })[];
    stats: {
      totalSpots: number;
      uniqueLeagues: number;
      totalTeams: number;
      progress: number;
    }
  } | null>(null)
  const [tripData, setTripData] = useState<Trip | null>(null)

  useEffect(() => {
    if (shareToken) {
      loadSharedData()
    }
  }, [shareToken])

  const loadSharedData = async () => {
    if (!shareToken) return

    try {
      setLoading(true)
      setError(null)

      // Get share link data
      const linkData = await getShareableLinkData(shareToken)
      
      if (!linkData) {
        setError('Share link not found or has expired')
        return
      }

      setShareData(linkData)

      if (linkData.type === 'group' && linkData.targetId) {
        // Load group data
        const groupShareData = await getGroupShareData(linkData.shareToken)
        setGroupData(groupShareData)
      } else if (linkData.type === 'all-time') {
        // Load user's all-time progress
        const userProgress = await getSharedUserProgress(linkData.createdBy)
        setUserData(userProgress)
      } else if (linkData.type === 'trip' && linkData.targetId) {
        // Load trip data
        const trip = await getTripById(linkData.targetId)
        setTripData(trip)
      }
    } catch (err) {
      console.error('Error loading shared data:', err)
      setError('Failed to load shared content')
    } finally {
      setLoading(false)
    }
  }

  const handleJoinGroup = async () => {
    if (!user?.id || !shareToken) return

    try {
      setIsJoining(true)
      const result = await joinGroup(
        shareToken,
        user.id,
        user.fullName || user.firstName || 'Anonymous'
      )

      if (result.success) {
        // Reload group data to show updated member list
        await loadSharedData()
      } else {
        setError(result.error || 'Failed to join group')
      }
    } catch (err) {
      console.error('Error joining group:', err)
      setError('Failed to join group')
    } finally {
      setIsJoining(false)
    }
  }

  const handleViewUser = async (userId: string) => {
    try {
      setLoadingUserTeams(true)
      setSelectedUser(userId)
      const userTeams = await getUserSpottedTeams(userId, 'all-time')
      setSelectedUserTeams(userTeams)
    } catch (err) {
      console.error('Error loading user teams:', err)
      setError('Failed to load user teams')
    } finally {
      setLoadingUserTeams(false)
    }
  }

  const closeUserModal = () => {
    setSelectedUser(null)
    setSelectedUserTeams([])
  }

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  const getTeamLogoUrl = (team: Team) => {
    // Use ESPN CDN for team logos
    if (team.league === 'NFL') {
      return `https://a.espncdn.com/i/teamlogos/nfl/500/${team.abbreviation?.toLowerCase()}.png`
    } else if (team.league === 'NBA') {
      return `https://a.espncdn.com/i/teamlogos/nba/500/${team.abbreviation?.toLowerCase()}.png`
    } else if (team.league === 'MLB') {
      return `https://a.espncdn.com/i/teamlogos/mlb/500/${team.abbreviation?.toLowerCase()}.png`
    } else if (team.league === 'NHL') {
      return `https://a.espncdn.com/i/teamlogos/nhl/500/${team.abbreviation?.toLowerCase()}.png`
    } else if (team.league === 'MLS') {
      return `https://a.espncdn.com/i/teamlogos/soccer/500/mls/${team.abbreviation?.toLowerCase()}.png`
    } else if (team.league === 'NCAA Division I') {
      return `https://a.espncdn.com/i/teamlogos/ncb/500/${team.abbreviation?.toLowerCase()}.png`
    } else if (team.league === 'Formula 1') {
      // F1 teams use placeholder since ESPN doesn't have F1 constructor logos
      return `https://via.placeholder.com/100x100/${team.primaryColor?.replace('#', '') || '42a5f5'}/white?text=${team.abbreviation || team.name.charAt(0)}`
    }
    return `https://via.placeholder.com/100x100/42a5f5/white?text=${team.abbreviation || team.name.charAt(0)}`
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-[#42a5f5] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading shared content...</p>
        </div>
      </div>
    )
  }

  if (error || !shareData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <Share2 className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Content Not Found</h1>
          <p className="text-gray-600 mb-6">
            {error || 'The shared link you\'re looking for doesn\'t exist or has expired.'}
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-[#42a5f5] hover:bg-[#1976d2]"
          >
            Go to SportSpot
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#42a5f5] to-[#1976d2] py-6 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/')}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {shareData.type === 'group' ? (groupData?.group?.name || 'Group Leaderboard') : 
                   shareData.type === 'all-time' ? 'Shared Progress' : 'Competition Trip'}
                </h1>
                <p className="text-blue-100">
                  {shareData.type === 'group' 
                    ? 'Team spotting leaderboard and competition'
                    : shareData.type === 'all-time' 
                    ? 'Team spotting achievements' 
                    : 'Live leaderboard and competition stats'
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-white text-sm">
                <div className="flex items-center space-x-1">
                  <Users className="h-4 w-4" />
                  <span>
                    {shareData.type === 'group' 
                      ? `${groupData?.members.length || 0} members`
                      : `${shareData.viewCount} views`
                    }
                  </span>
                </div>
              </div>
              {shareData.type === 'group' && user ? (
                groupData?.members.some(member => member.userId === user.id) ? (
                  <Button
                    disabled
                    className="bg-green-600 text-white"
                  >
                    <Trophy className="h-4 w-4 mr-2" />
                    Already a Member
                  </Button>
                ) : (
                  <Button
                    onClick={handleJoinGroup}
                    disabled={isJoining}
                    className="bg-white text-[#42a5f5] hover:bg-gray-100"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    {isJoining ? 'Joining...' : 'Join Group'}
                  </Button>
                )
              ) : (
                <Button
                  onClick={() => navigate('/sign-up')}
                  className="bg-white text-[#42a5f5] hover:bg-gray-100"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Join SportSpot
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {shareData.type === 'group' && groupData?.group ? (
          /* Group Leaderboard View */
          <div className="space-y-8">
            {/* Group Info */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {groupData.group.name}
                  </h2>
                  {groupData.group.description && (
                    <p className="text-gray-600">{groupData.group.description}</p>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-[#42a5f5]">
                    {groupData.members.length}
                  </div>
                  <p className="text-sm text-gray-600">Members</p>
                </div>
              </div>

              {/* Group Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Trophy className="h-8 w-8 text-[#42a5f5] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">
                    {groupData.leaderboard.reduce((sum, entry) => sum + entry.totalSpots, 0)}
                  </div>
                  <div className="text-sm text-gray-600">Total Spots</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Users className="h-8 w-8 text-[#42a5f5] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">{groupData.members.length}</div>
                  <div className="text-sm text-gray-600">Members</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Award className="h-8 w-8 text-[#42a5f5] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">
                    {groupData.leaderboard[0]?.totalSpots || 0}
                  </div>
                  <div className="text-sm text-gray-600">Top Score</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Calendar className="h-8 w-8 text-[#42a5f5] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">
                    {formatDate(groupData.group.createdAt).split(',')[0]}
                  </div>
                  <div className="text-sm text-gray-600">Created</div>
                </div>
              </div>
            </div>

            {/* Leaderboard */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Group Leaderboard
              </h3>
              <div className="space-y-3">
                {groupData.leaderboard.map((entry, index) => (
                  <div
                    key={entry.userId}
                    onClick={() => handleViewUser(entry.userId)}
                    className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${
                      index === 0 ? 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100' :
                      index === 1 ? 'bg-gray-50 border-gray-200 hover:bg-gray-100' :
                      index === 2 ? 'bg-orange-50 border-orange-200 hover:bg-orange-100' :
                      'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                        index === 0 ? 'bg-yellow-500' :
                        index === 1 ? 'bg-gray-500' :
                        index === 2 ? 'bg-orange-500' :
                        'bg-[#42a5f5]'
                      }`}>
                        {entry.rank}
                      </div>
                      
                      {/* Profile Image */}
                      <div className="flex-shrink-0">
                        {entry.profileImageUrl ? (
                          <img
                            src={entry.profileImageUrl}
                            alt={`${entry.userName}'s profile`}
                            className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                              const fallback = target.nextElementSibling as HTMLElement
                              if (fallback) fallback.style.display = 'flex'
                            }}
                          />
                        ) : null}
                        <div 
                          className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold border-2 border-gray-200 ${
                            entry.profileImageUrl ? 'hidden' : 'flex'
                          } ${index === 0 ? 'bg-yellow-600' :
                                index === 1 ? 'bg-gray-600' :
                                index === 2 ? 'bg-orange-600' :
                                'bg-[#42a5f5]'}`}
                          style={{ display: entry.profileImageUrl ? 'none' : 'flex' }}
                        >
                          {entry.userName.charAt(0).toUpperCase()}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 flex items-center space-x-2">
                          <span>{entry.userName}</span>
                          {entry.isCreator && (
                            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                              Creator
                            </span>
                          )}
                        </h4>
                        <p className="text-gray-600 text-sm">
                          Member since {formatDate(entry.joinedAt).split(',')[0]}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex items-center space-x-3">
                      <div>
                        <div className="text-2xl font-bold text-gray-900">
                          {entry.totalSpots}
                        </div>
                        <div className="text-sm text-gray-600">Teams Spotted</div>
                      </div>
                      <Eye className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>

              {groupData.leaderboard.length === 0 && (
                <div className="text-center py-8">
                  <Trophy className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No Activity Yet
                  </h3>
                  <p className="text-gray-600">
                    Start spotting teams to appear on the leaderboard!
                  </p>
                </div>
              )}
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-[#42a5f5] to-[#1976d2] rounded-lg p-8 text-center text-white">
              <h3 className="text-2xl font-bold mb-4">Ready to Compete?</h3>
              <p className="mb-6 opacity-90">
                {user && groupData.members.some(member => member.userId === user.id)
                  ? 'Start spotting teams to climb the leaderboard!'
                  : 'Join this group and start competing with friends!'
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {user ? (
                  groupData.members.some(member => member.userId === user.id) ? (
                    <Button
                      onClick={() => navigate('/dashboard')}
                      className="bg-white text-[#42a5f5] hover:bg-gray-100"
                    >
                      <Trophy className="h-4 w-4 mr-2" />
                      Start Spotting Teams
                    </Button>
                  ) : (
                    <Button
                      onClick={handleJoinGroup}
                      disabled={isJoining}
                      className="bg-white text-[#42a5f5] hover:bg-gray-100"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      {isJoining ? 'Joining...' : 'Join This Group'}
                    </Button>
                  )
                ) : (
                  <Button
                    onClick={() => navigate('/sign-up')}
                    className="bg-white text-[#42a5f5] hover:bg-gray-100"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Sign Up to Join
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-[#42a5f5]"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Learn More
                </Button>
              </div>
            </div>
          </div>
        ) : shareData.type === 'all-time' && userData ? (
          /* All-Time Progress View */
          <div className="space-y-8">
            {/* User Stats */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {userData.user?.displayName || 'SportSpot User'}
                  </h2>
                  <p className="text-gray-600">All-Time Team Spotting Progress</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-[#42a5f5]">
                    {userData.stats.progress}%
                  </div>
                  <p className="text-sm text-gray-600">Complete</p>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Trophy className="h-8 w-8 text-[#42a5f5] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">{userData.stats.totalSpots}</div>
                  <div className="text-sm text-gray-600">Teams Spotted</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Award className="h-8 w-8 text-[#42a5f5] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">{userData.stats.uniqueLeagues}</div>
                  <div className="text-sm text-gray-600">Leagues</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Users className="h-8 w-8 text-[#42a5f5] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">{userData.stats.totalTeams}</div>
                  <div className="text-sm text-gray-600">Total Teams</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <TrendingUp className="h-8 w-8 text-[#42a5f5] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">{userData.stats.progress}%</div>
                  <div className="text-sm text-gray-600">Progress</div>
                </div>
              </div>
            </div>

            {/* Recent Teams */}
            {userData.spottedTeams.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Recently Spotted Teams
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {userData.spottedTeams.slice(0, 12).map((team, index) => (
                    <div key={`${team.id}-${index}`} className="border border-green-300 rounded-lg p-4 bg-green-50">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 pr-2">
                          <h4 className="font-bold text-gray-900 leading-tight">{team.name}</h4>
                          <p className="text-gray-600 text-sm mt-1">{team.city}</p>
                        </div>
                        <div className="w-12 h-12 bg-white rounded-lg p-1 flex-shrink-0 shadow-sm">
                          <img
                            src={getTeamLogoUrl(team)}
                            alt={`${team.name} logo`}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                              const fallback = target.nextElementSibling as HTMLElement
                              if (fallback) fallback.style.display = 'flex'
                            }}
                          />
                          <div 
                            className="w-full h-full bg-[#42a5f5] rounded text-white text-xs font-bold flex items-center justify-center"
                            style={{ display: 'none' }}
                          >
                            {team.abbreviation || team.name.charAt(0)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                          {team.league}
                        </span>
                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDate(team.spottedAt)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {userData.spottedTeams.length > 12 && (
                  <div className="mt-4 text-center">
                    <p className="text-gray-500">
                      And {userData.spottedTeams.length - 12} more teams spotted!
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-[#42a5f5] to-[#1976d2] rounded-lg p-8 text-center text-white">
              <h3 className="text-2xl font-bold mb-4">Start Your Own Journey</h3>
              <p className="mb-6 opacity-90">
                Join SportSpot and compete with friends to spot teams around the world!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/sign-up')}
                  className="bg-white text-[#42a5f5] hover:bg-gray-100"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Sign Up Free
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-[#42a5f5]"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Learn More
                </Button>
              </div>
            </div>
          </div>
        ) : shareData.type === 'trip' && tripData ? (
          /* Trip Competition View */
          <div className="space-y-8">
            {/* Trip Header */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900">{tripData.name}</h2>
                  {tripData.description && (
                    <p className="text-gray-600 mt-2">{tripData.description}</p>
                  )}
                  
                  <div className="flex items-center space-x-4 mt-4 text-sm text-gray-500">
                    <div className="flex items-center space-x-1">
                      <Users className="h-4 w-4" />
                      <span>{tripData.participants.length} participants</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>Started {formatDate(tripData.createdAt)}</span>
                    </div>
                    {tripData.isActive && (
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>Active</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="text-right">
                  <Button
                    onClick={() => navigate(`/trip?join=${shareData.shareToken}`)}
                    className="bg-[#42a5f5] hover:bg-[#1976d2]"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Join Competition
                  </Button>
                </div>
              </div>
            </div>

            {/* Trip Leaderboard */}
            <Leaderboard
              type="trip"
              tripId={tripData.id}
            />

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-[#42a5f5] to-[#1976d2] rounded-lg p-8 text-center text-white">
              <h3 className="text-2xl font-bold mb-4">Ready to Compete?</h3>
              <p className="mb-6 opacity-90">
                Join this trip and start spotting teams to climb the leaderboard!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate(`/trip?join=${shareData.shareToken}`)}
                  className="bg-white text-[#42a5f5] hover:bg-gray-100"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Join This Trip
                </Button>
                <Button
                  onClick={() => navigate('/sign-up')}
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-[#42a5f5]"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Sign Up Free
                </Button>
              </div>
            </div>
          </div>
        ) : (
          /* Loading or Error State */
          <div className="text-center py-16">
            <Share2 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Content Not Available
            </h2>
            <p className="text-gray-600 mb-6">
              The shared content could not be loaded at this time.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-[#42a5f5] hover:bg-[#1976d2]"
            >
              Go to SportSpot
            </Button>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {groupData?.leaderboard.find(entry => entry.userId === selectedUser)?.userName || 'User'}'s Teams
                </h2>
                <p className="text-gray-600">
                  {selectedUserTeams.length} teams spotted
                </p>
              </div>
              <Button
                onClick={closeUserModal}
                variant="outline"
                size="sm"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="p-6">
              {loadingUserTeams ? (
                <div className="text-center py-8">
                  <div className="animate-spin h-12 w-12 border-4 border-[#42a5f5] border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading teams...</p>
                </div>
              ) : selectedUserTeams.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedUserTeams.map((team, index) => (
                    <div key={`${team.id}-${index}`} className="border border-green-300 rounded-lg p-4 bg-green-50">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 pr-2">
                          <h4 className="font-bold text-gray-900 leading-tight">{team.name}</h4>
                          <p className="text-gray-600 text-sm mt-1">{team.city}</p>
                        </div>
                        <div className="w-12 h-12 bg-white rounded-lg p-1 flex-shrink-0 shadow-sm">
                          <img
                            src={getTeamLogoUrl(team)}
                            alt={`${team.name} logo`}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                              const fallback = target.nextElementSibling as HTMLElement
                              if (fallback) fallback.style.display = 'flex'
                            }}
                          />
                          <div 
                            className="w-full h-full bg-[#42a5f5] rounded text-white text-xs font-bold flex items-center justify-center"
                            style={{ display: 'none' }}
                          >
                            {team.abbreviation || team.name.charAt(0)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                          {team.league}
                        </span>
                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDate(team.spottedAt)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Trophy className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No Teams Spotted Yet
                  </h3>
                  <p className="text-gray-600">
                    This user hasn't spotted any teams yet.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}